/**
 * 预加载工具
 * 用于预加载组件和资源，提升用户体验
 */

class PreloadManager {
    constructor() {
        this.preloadedComponents = new Set();
        this.preloadPromises = new Map();
    }

    /**
     * 预加载组件
     * @param {string} componentName - 组件名称
     * @param {Function} importFunction - 动态import函数
     */
    preloadComponent(componentName, importFunction) {
        if (this.preloadedComponents.has(componentName)) {
            return this.preloadPromises.get(componentName);
        }

        console.log(`预加载组件: ${componentName}`);
        const promise = importFunction().catch(error => {
            console.error(`预加载组件失败 ${componentName}:`, error);
            this.preloadedComponents.delete(componentName);
            this.preloadPromises.delete(componentName);
        });

        this.preloadedComponents.add(componentName);
        this.preloadPromises.set(componentName, promise);

        return promise;
    }

    /**
     * 预加载图片
     * @param {string} src - 图片URL
     */
    preloadImage(src) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.onerror = reject;
            img.src = src;
        });
    }

    /**
     * 预加载多个图片
     * @param {string[]} srcs - 图片URL数组
     */
    preloadImages(srcs) {
        return Promise.all(srcs.map(src => this.preloadImage(src)));
    }

    /**
     * 预加载CSS文件
     * @param {string} href - CSS文件URL
     */
    preloadCSS(href) {
        return new Promise((resolve, reject) => {
            const link = document.createElement('link');
            link.rel = 'preload';
            link.as = 'style';
            link.href = href;
            link.onload = resolve;
            link.onerror = reject;
            document.head.appendChild(link);
        });
    }

    /**
     * 智能预加载 - 基于用户行为预测
     * @param {string} trigger - 触发条件 ('hover', 'visible', 'idle')
     * @param {Function} preloadFunction - 预加载函数
     * @param {number} delay - 延迟时间（毫秒）
     */
    smartPreload(trigger, preloadFunction, delay = 0) {
        const executePreload = () => {
            setTimeout(() => {
                preloadFunction();
            }, delay);
        };

        switch (trigger) {
            case 'idle':
                if ('requestIdleCallback' in window) {
                    requestIdleCallback(executePreload);
                } else {
                    setTimeout(executePreload, 100);
                }
                break;
            case 'visible':
                // 当页面可见时预加载
                if (document.visibilityState === 'visible') {
                    executePreload();
                } else {
                    document.addEventListener('visibilitychange', () => {
                        if (document.visibilityState === 'visible') {
                            executePreload();
                        }
                    }, { once: true });
                }
                break;
            default:
                executePreload();
        }
    }

    /**
     * 网络状态感知预加载
     * @param {Function} preloadFunction - 预加载函数
     */
    networkAwarePreload(preloadFunction) {
        // 检查网络连接状态
        if ('connection' in navigator) {
            const connection = navigator.connection;
            const slowConnection = connection.effectiveType === 'slow-2g' ||
                connection.effectiveType === '2g' ||
                connection.saveData;

            if (!slowConnection) {
                this.smartPreload('idle', preloadFunction);
            }
        } else {
            // 如果无法检测网络状态，默认预加载
            this.smartPreload('idle', preloadFunction);
        }
    }
}

// 创建全局实例
const preloadManager = new PreloadManager();

// 预定义的预加载任务
export const preloadTasks = {
    // 预加载管理页面
    managementPage: () => preloadManager.preloadComponent(
        'EInvoiceSettingsPage',
        () => import('../pages/management')
    ),

    // 预加载管理工具页面
    adminToolsPage: () => preloadManager.preloadComponent(
        'AdminTools',
        () => import('../pages/management/AdminTools')
    ),

    // 预加载所有管理相关页面
    allManagementPages: () => Promise.all([
        preloadTasks.managementPage(),
        preloadTasks.adminToolsPage()
    ])
};

export default preloadManager; 